import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, Contact } from '../api';
import { useSession } from '../session';
import { ErrorBox, SourceBadge, useAsync } from '../components/ui';
import { Timeline } from '../components/Timeline';
import { AgentPanel } from '../components/AgentPanel';
import { ContactForm, ContactInput } from './ContactForm';
import { TaskList } from '../components/TaskList';
import { EmailCompose } from '../components/EmailCompose';

export function ContactPage() {
  const { id } = useParams();
  const s = useSession();
  const nav = useNavigate();
  const [k, setK] = useState<Contact | null>(null);
  const [editing, setEditing] = useState(false);
  const [tick, setTick] = useState(0);
  const [composing, setComposing] = useState(false);
  const load = useAsync();
  const save = useAsync();
  const reload = () => load.run(async () => setK(await api<Contact>(`/contacts/${id}`)));
  useEffect(() => { reload(); setEditing(false); }, [id]);
  const update = async (v: ContactInput) => { if (await save.run(() => api(`/contacts/${id}`, { method: 'PATCH', body: v }))) { setEditing(false); reload(); } };
  const archive = async () => {
    if (!window.confirm('Archiválod a kontaktot?')) return;
    if (await save.run(() => api(`/contacts/${id}/archive`, { method: 'POST' }))) nav('/kontaktok');
  };
  if (load.error) return <ErrorBox error={load.error} />;
  if (!k) return <div className="muted">Betöltés…</div>;
  const fs = k.field_sources;
  return (
    <div className="detail">
      <div className="page-head">
        <h2>{k.full_name}</h2>
        <div className="spacer" />
        {k.email && <button className="secondary" onClick={() => setComposing(true)}>E-mail írása</button>}
        {!editing && <button onClick={() => setEditing(true)}>Szerkesztés</button>}
        {s.role === 'owner' && <button className="danger" onClick={archive}>Archiválás</button>}
      </div>
      {composing && <EmailCompose initial={{ to: k.email ?? '', body: k.addressing === 'tegez' ? `Szia ${k.first_name ?? ''}!\n\n` : `Tisztelt ${k.full_name}!\n\n` }}
        subjectRef={{ type: 'contact', id: k.id }} onClose={() => setComposing(false)} onSent={() => setTick((x) => x + 1)} />}
      <div className="cols">
        <div>
        <section className="card">
          {editing ? <ContactForm initial={k} onSubmit={update} onCancel={() => setEditing(false)} error={save.error} busy={save.busy} /> : (
            <dl className="props">
              <dt>Cég</dt><dd>{k.company_id ? <Link to={`/cegek/${k.company_id}`}>{k.company_name}</Link> : '–'}</dd>
              <dt>Pozíció</dt><dd>{k.position ?? '–'} <SourceBadge src={fs.position} /></dd>
              <dt>E-mail</dt><dd>{k.email ? <a href={`mailto:${k.email}`}>{k.email}</a> : '–'} <SourceBadge src={fs.email} /></dd>
              <dt>Telefon</dt><dd>{k.phone ? <a href={`tel:${k.phone}`}>{k.phone}</a> : '–'} <SourceBadge src={fs.phone} /></dd>
              <dt>Megszólítás</dt><dd>{k.addressing === 'tegez' ? 'tegezés' : 'magázás'}</dd>
              <dt>Címkék</dt><dd>{k.tags.length ? k.tags.map((t) => <span key={t} className="tag">{t}</span>) : '–'}</dd>
              <dt>Összefoglaló</dt><dd className="pre">{k.summary ?? '–'}</dd>
            </dl>
          )}
        </section>
        <TaskList subject={{ type: 'contact', id: k.id }} reloadKey={tick} onChange={() => setTick((x) => x + 1)} />
        </div>
        <div>
          {s.on('ai.agents') && <AgentPanel subject={{ type: 'contact', id: k.id }} onChange={() => { reload(); setTick((x) => x + 1); }} />}
          <Timeline path={`/contacts/${k.id}`} reloadKey={tick} />
        </div>
      </div>
    </div>
  );
}
